import {useQueries, type UseQueryOptions} from '@github-ui/react-query'
import {useCallback, useMemo, useRef, useState} from 'react'
import {
  DeferredItemSchema,
  type DeferredItem,
  type EnrichedPull,
} from '@github-ui/shared-dashboard-lists/pulls/types/list-types'
import type {SectionDataResult} from '../types/routes-types'
import {useAnalytics} from '@github-ui/use-analytics'
import {fetchSectionDeferredData} from '../helpers/section-fetchers'
import {createPersister} from '@github-ui/react-query-persister'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'
import {
  type BatchedUseQueryOptions,
  requiredBatchedQueryResult,
  useBatchedQueries,
} from '@github-ui/use-batched-queries'
import {isFeatureEnabled} from '@github-ui/feature-flags'

const SECTION_STALE_TIME = 30_000 // 30 seconds
const LOAD_MORE_INCREMENT = 10
const MAX_SECTION_RESULTS = 25

interface UseSectionDataOptions {
  sectionId: string
  scopeKey: string
  primaryData: SectionDataResult | undefined
  initialVisibleCount: number
  isCollapsed?: boolean
}

interface UseSectionDataResult {
  enrichedPulls: EnrichedPull[]
  hasMore: boolean
  isDeferredError: boolean
  loadMore: () => void
}

const deferredItemValidator = Compile(DeferredItemSchema)

type DeferredItemVariables = Pick<DeferredItem, 'id' | 'globalId'>

export function useSectionData({
  sectionId,
  scopeKey,
  primaryData,
  initialVisibleCount,
  isCollapsed,
}: UseSectionDataOptions): UseSectionDataResult {
  const {sendAnalyticsEvent} = useAnalytics()

  const [visibleCount, setVisibleCount] = useState(initialVisibleCount)

  const [prevScopeKey, setPrevScopeKey] = useState(scopeKey)
  if (scopeKey !== prevScopeKey) {
    setVisibleCount(initialVisibleCount)
    setPrevScopeKey(scopeKey)
  }

  const primaryResults = useMemo(() => primaryData?.results.slice(0, MAX_SECTION_RESULTS) ?? [], [primaryData?.results])
  const visibleResults = useMemo(
    () => (isCollapsed ? [] : primaryResults.slice(0, visibleCount)),
    [primaryResults, visibleCount, isCollapsed],
  )

  const batchedFetchDeferredItem = useBatchedFetchDeferredItem()

  const persister = useMemo(
    () => createPersister({sendAnalyticsEvent, validator: deferredItemValidator}),
    [sendAnalyticsEvent],
  )

  const newBatchedQueriesHookEnabled = isFeatureEnabled('new_batched_queries_hook')

  const oldDeferredItemQueries = useQueries({
    queries: visibleResults.map<UseQueryOptions<DeferredItem>>(pull => ({
      queryKey: ['inbox-section-deferred-item', pull.id],
      queryFn: async ({signal}) => batchedFetchDeferredItem(pull, signal),
      staleTime: SECTION_STALE_TIME,
      persister,
      enabled: !newBatchedQueriesHookEnabled,
    })),

    combine: results => ({
      data: new Map(results.map((result, i) => [visibleResults[i]?.id, result])),
      isError: results.some(result => result.isError),
    }),
  })

  const newDeferredItemQueries = useBatchedQueries({
    queries: visibleResults.map<BatchedUseQueryOptions<DeferredItem, DeferredItemVariables>>(pull => ({
      queryKey: ['inbox-section-deferred-item', pull.id, pull.globalId],
      variables: {id: pull.id, globalId: pull.globalId},
      staleTime: SECTION_STALE_TIME,
      persister,
      enabled: newBatchedQueriesHookEnabled,
    })),
    batchedQueryFn: async items => {
      const batchItems = await fetchSectionDeferredData({prIds: items.map(item => item.id)})
      const batchMap = new Map(
        batchItems.map(result => [getListItemIdentity({...result, itemType: 'pull_request'}), result]),
      )
      return items.map(item =>
        requiredBatchedQueryResult(batchMap.get(getListItemIdentity({...item, itemType: 'pull_request'}))),
      )
    },
    combine: results => ({
      data: new Map(results.map((result, i) => [visibleResults[i]?.id, result])),
      isError: results.some(result => result.isError),
    }),
  })

  const deferredItemQueries = newBatchedQueriesHookEnabled ? newDeferredItemQueries : oldDeferredItemQueries

  const enrichedPulls = useMemo<EnrichedPull[]>(
    () =>
      visibleResults.map(pull => {
        const result = deferredItemQueries.data.get(pull.id)
        return {
          ...pull,
          reviewDecisionState: result?.data?.reviewDecisionState,
          statusCheckRollup: result?.data?.statusCheckRollup,
          approvedReviewsCount: result?.data?.approvedReviewsCount,
          changesRequestedReviewsCount: result?.data?.changesRequestedReviewsCount,
          deferredDataStatus: result?.status,
        }
      }),
    [visibleResults, deferredItemQueries.data],
  )

  const loadMore = useCallback(() => {
    sendAnalyticsEvent('inbox.click', 'LOAD_MORE_BUTTON', {section: sectionId})
    setVisibleCount(count => count + LOAD_MORE_INCREMENT)
  }, [sendAnalyticsEvent, sectionId])

  return {
    enrichedPulls,
    hasMore: primaryResults.length > visibleResults.length,
    isDeferredError: deferredItemQueries.isError,
    loadMore,
  }
}

/**
 * Wrapper around `fetchSectionDeferredData` that acts like a single-item request, but internally batches all requests
 * within a 10ms window, combining them into a single call.
 * @deprecated
 */
function useBatchedFetchDeferredItem() {
  const currentBatchRef = useRef<{
    readonly ids: Set<DeferredItem['id']>
    readonly promise: Promise<Map<string, DeferredItem>>
  } | null>(null)

  return useCallback(async (item: Pick<DeferredItem, 'id' | 'globalId'>, abortSignal: AbortSignal) => {
    // Just to be safe, check if the request was already aborted before we started
    abortSignal.throwIfAborted()

    const id = item.id
    const ids = currentBatchRef.current?.ids ?? new Set<DeferredItem['id']>()
    ids.add(id)
    abortSignal.addEventListener('abort', () => ids.delete(id), {once: true})

    // Queue a batch if necessary
    if (!currentBatchRef.current) {
      async function executeBatch() {
        // Short delay to collect batched IDs
        await new Promise(resolve => setTimeout(resolve, 10))

        // Immediately clear current batch so a new one can be started while this request is running
        currentBatchRef.current = null

        // `ids` will be empty if all requests were aborted before the batch executed (eg, the component unmounted)
        // No need to throw here; we'll check later and throw abort or missing data errors per request.
        if (ids.size === 0) return new Map<never, never>()

        const batchItems = await fetchSectionDeferredData({prIds: Array.from(ids)})
        return new Map(batchItems.map(result => [getListItemIdentity({...result, itemType: 'pull_request'}), result]))
      }

      currentBatchRef.current = {ids, promise: executeBatch()}
    }

    const batchMap = await currentBatchRef.current.promise

    // If this item was aborted before the request, it will be missing from the results but we still want a more
    // specific error. It's also possible that it was aborted during the request.
    abortSignal.throwIfAborted()

    const result = batchMap.get(getListItemIdentity({...item, itemType: 'pull_request'}))
    if (!result) throw new Error(`Item with id ${id} not found in response data`)

    return result
  }, [])
}
